import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from './Navbar';
import SEO from './SEO';
import Transition from './Transition';
import ContactSection from './ContactSection';
import { getInsightBySlug } from '../data/insights';
import useReveal from '../hooks/useReveal';

const SITE_URL = 'https://portfolio.skdesign.be';

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="font-bricolage font-extrabold text-mainkleur text-[24px] sm:text-[30px] lg:text-[36px] leading-[1.05] tracking-tight mt-10 sm:mt-12">
          {block.text}
        </h2>
      );
    case 'list':
      return (
        <ul className="list-disc pl-5 space-y-2 marker:text-mainkleur">
          {block.items.map((item, i) => (
            <li key={i} className="font-space font-medium text-mainkleur text-base sm:text-lg leading-[1.55]">
              {item}
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote className="border-l-4 border-verloop-oranje-mid pl-5 py-1 my-2">
          <p className="font-bricolage font-semibold text-mainkleur text-[20px] sm:text-[24px] lg:text-[28px] leading-[1.25]">
            {block.text}
          </p>
        </blockquote>
      );
    case 'image':
      if (!block.src) return null;
      return (
        <figure className="my-8 sm:my-10">
          <div className="overflow-hidden rounded-2xl bg-mainkleur/5">
            <img
              src={block.src}
              alt={block.alt || ''}
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
          {block.caption && (
            <figcaption className="mt-3 font-space text-mainkleur/70 text-sm leading-[1.5]">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case 'paragraph':
    default:
      return (
        <p className="font-space font-medium text-mainkleur text-base sm:text-lg leading-[1.6]">
          {block.text}
        </p>
      );
  }
};

const InsightPage = () => {
  const { slug } = useParams();
  const insight = getInsightBySlug(slug);
  const bodyRef = useReveal({ threshold: 0.1, stagger: 80 });

  if (!insight) {
    return (
      <>
        <SEO
          title="Insight not found"
          description="This article could not be found."
          path="/insights"
          noIndex
        />
        <Navbar />
        <main className="bg-[#f2f3d9] min-h-[70vh]">
          <section className="pt-28 sm:pt-32 lg:pt-40 pb-20">
            <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
              <h1 className="font-anton text-mainkleur text-[12vw] sm:text-[80px] lg:text-[120px] leading-[0.9] uppercase">
                Not found
              </h1>
              <p className="mt-6 font-space text-mainkleur text-lg leading-[1.5] max-w-[560px]">
                The article you are looking for does not exist or may have been moved.
              </p>
              <Link
                to="/insights"
                className="mt-8 inline-flex items-center gap-3 bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur rounded-full px-6 py-4 font-bricolage font-semibold text-lg uppercase tracking-tight hover:scale-[1.03] transition-all duration-300"
              >
                Back to insights
              </Link>
            </div>
          </section>
          <ContactSection />
        </main>
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: insight.title,
    description: insight.description,
    datePublished: insight.date,
    url: `${SITE_URL}/insights/${insight.slug}`,
    author: {
      '@type': 'Person',
      name: 'Sander Kuyken',
      url: `${SITE_URL}/`,
    },
    keywords: insight.tag,
  };

  return (
    <>
      <SEO
        title={insight.title}
        description={insight.description}
        path={`/insights/${insight.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="bg-[#f2f3d9]">
        {/* Article header */}
        <section className="pt-28 sm:pt-32 lg:pt-40 pb-10 sm:pb-12">
          <div className="max-w-[860px] mx-auto px-5 sm:px-6 lg:px-8">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 font-space text-mainkleur/60 text-xs sm:text-sm uppercase tracking-[0.2em] hover:text-mainkleur transition-colors duration-300"
            >
              <span aria-hidden="true">&larr;</span> Insights
            </Link>

            <div className="mt-6 flex items-center gap-4 font-space text-mainkleur/55 text-xs sm:text-sm uppercase tracking-[0.18em]">
              <span className="inline-flex items-center bg-mainkleur/10 text-mainkleur border border-mainkleur/20 rounded-full px-3 py-1 font-semibold tracking-[0.12em]">
                {insight.tag}
              </span>
              <span>{insight.displayDate}</span>
              <span aria-hidden="true">·</span>
              <span>{insight.readTime}</span>
            </div>

            <h1 className="mt-6 font-anton text-mainkleur text-[10vw] sm:text-[56px] lg:text-[72px] leading-[0.95] tracking-[-1px] sm:tracking-[-2px] uppercase">
              {insight.title}
            </h1>

            <p className="mt-6 font-space text-mainkleur/80 text-lg sm:text-xl leading-[1.5]">
              {insight.description}
            </p>

            <div className="mt-10 w-full h-px bg-mainkleur/30" />
          </div>
        </section>

        {/* Hero cover */}
        {insight.cover && (
          <section className="pb-10 sm:pb-14">
            <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-8">
              <div className="overflow-hidden rounded-2xl sm:rounded-3xl bg-mainkleur/5 aspect-[16/9]">
                <img
                  src={insight.cover}
                  alt={`${insight.title} — cover`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>
        )}

        {/* Article body */}
        <section className="pb-20 sm:pb-28 lg:pb-32">
          <div className="max-w-[860px] mx-auto px-5 sm:px-6 lg:px-8">
            <article ref={bodyRef} className="space-y-5 sm:space-y-6">
              {insight.content.map((block, i) => (
                <div key={i} className="reveal-up">
                  <ContentBlock block={block} />
                </div>
              ))}
            </article>
          </div>
        </section>

        <ContactSection />
      </main>
    </>
  );
};

export default Transition(InsightPage);
